import React from "react";
import { Chart } from "react-google-charts";
import { processSharedColumnChartColors } from "../SharedBaseChartFunctions";
import { processDoubleColumnChartData } from "./DoubleColumnChartFunctions";
import { IDoubleColumnChartData } from "./IDoubleColumnChartData";
import "../../chartProperty.scss";
import { columnSegmentColumnChart } from "../../../../../../Helpers/Chart/chartSegmentClickHelper";
import { GoogleGroupedStackedChartNoAnimationOptions, GoogleGroupedStackedChartDefaultOptions, GoogleGroupedStackedMinMaxChartDefaultOptions, GoogleGroupedStackedChartMinMaxNoAnimationOptions } from "../../../../Constants/Chart/GoogleChartDefaultOptions";
import { ResultWithValue } from "../../../../Constants/Result/ResultWithValue";
import { RenderTabOnFocus } from "../../../../Events/RenderTabOnFocus";
import { SmallLoading } from "../../../Loading/IntermediateLoading";
import { RenderFromServiceCall } from "../RenderFromServiceCall";
import { AlertMessage } from "../../../Alerts/alertMessage";
import { MDBox } from "../../../MDBox";
import { Typography } from "@mui/material";


interface IProps {
  height?: string;
  fetchData: () => Promise<ResultWithValue<IDoubleColumnChartData>>;
  onSegmentClick?: (label: string) => void;
  hash?: string;
  isStacked?: boolean;
  disableAnimation?: boolean;
  chartOptions?: any;
  emptyMessage? : React.ReactNode;
}

/**
 * Bar chart with 2 columns grouped. Used on the Points available / Points earned chart
 * Labels should be a list of groups
 * Each dataset should have a list of item in data that is the same length as Labels
 * Each dataset represents a column with in the groups
 **/
export const DoubleColumnChart: React.FC<IProps> = (props: IProps) => {
  return (
    <RenderFromServiceCall
      key={props.hash ?? "DoubleColumnChart"}
      fetchData={props.fetchData}
      renderFunction={(data: IDoubleColumnChartData) => {
        const chartData = processDoubleColumnChartData(data, false);
        var addMinMaxStyling = false;
        let lstValues: Array<any> = [];
        data.datasets.forEach(x => lstValues.push(x.data));
        addMinMaxStyling = Math.max(...lstValues.flat()) === 0 ? true : false;
        let googleChartOpts = GoogleGroupedStackedChartDefaultOptions;
        if (window.location.href.includes("disableAnimation") || props.chartOptions?.isPowerPointPrint) {
          if (addMinMaxStyling) {
            googleChartOpts = GoogleGroupedStackedChartMinMaxNoAnimationOptions;
          }
          else {
            googleChartOpts = GoogleGroupedStackedChartNoAnimationOptions;
          }
        }
        else {
          if (addMinMaxStyling) {
            googleChartOpts = GoogleGroupedStackedMinMaxChartDefaultOptions;
          }
        }
        if (lstValues.length === 0) {
          return (
            props.emptyMessage === undefined ? (
              <AlertMessage type="warning" message="No data to display" />
            ) : (
              <MDBox style={{ textAlign: 'center' }}>
                <img
                  src={`${process.env.PUBLIC_URL}/img/verificationProcess/Elements/NoData.svg`}
                  alt={'No Data'}
                  style={{ maxWidth: '25%', marginTop: '10px' }}
                />
                <MDBox>
                  <Typography variant="h4" mt={2} mb={1}>
                    Oh dear!
                  </Typography>
                  <Typography variant="body2" color="secondary" component="span">
                    <p>{props.emptyMessage}</p>
                  </Typography>
                </MDBox>
              </MDBox>
            )
          );
        }
        const googleChartOptions = googleChartOpts;
        return (
          <RenderTabOnFocus>
            <Chart
              width="99%"
              chartType="ColumnChart"
              loader={<SmallLoading />}
              height={props.height ?? "375px"}
              data={chartData}
              options={{
                ...googleChartOptions,
                colors: processSharedColumnChartColors(data),
                isStacked: props.isStacked,
              }}
              rootProps={{ "data-testid": "1" }}
              chartEvents={[
                {
                  eventName: "select",
                  callback: columnSegmentColumnChart(
                    data.labels,
                    props.onSegmentClick,
                    chartData
                  ),
                },
              ]}
            />
          </RenderTabOnFocus>
        );
      }}
    />
  );
};
